import { TagSelect, TextField } from '@ads/components-react';
import { PlusIcon } from '@heroicons/react/24/outline';
import { type KeyboardEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { addKeyTerm, parseKeyTerms, serializeKeyTerms } from '@/shared/lib/examItemSubtype';

/** Props do KeyTermsInput. */
export interface KeyTermsInputProps {
  /** Termos separados por vírgula, no formato do campo `KeyTerms` da API. */
  readonly value: string;
  /** Notifica a nova string serializada a cada inclusão/remoção de termo. */
  readonly onChange: (value: string) => void;
  /** Rótulo do campo. */
  readonly label: string;
  /** Placeholder do input de digitação. */
  readonly placeholder?: string;
  /** Texto auxiliar exibido abaixo do input. */
  readonly helper?: string;
  /** Limite de caracteres da string serializada (contrato do backend). */
  readonly maxLength: number;
}

/**
 * Entrada de termos-chave em formato de chips.
 *
 * Os termos não vêm de nenhum catálogo — são texto livre por questão —, então em
 * vez de um multiselect o campo usa digitação com confirmação por `Enter` ou
 * vírgula. Cada termo vira um chip removível, e o valor propagado ao formulário
 * é a string separada por vírgula que o backend espera em `KeyTerms`.
 *
 * Termos vazios, duplicados (ignorando caixa) e os que estourariam `maxLength`
 * são recusados silenciosamente.
 *
 * @example
 * ```tsx
 * <KeyTermsInput
 *   value={field.value ?? ''}
 *   onChange={field.onChange}
 *   label="Termos-chave"
 *   maxLength={1000}
 * />
 * ```
 */
export function KeyTermsInput({
  value,
  onChange,
  label,
  placeholder,
  helper,
  maxLength,
}: KeyTermsInputProps) {
  const { t } = useTranslation('questionLibrary');
  const [draft, setDraft] = useState('');

  const terms = parseKeyTerms(value);

  function commitDraft(candidate: string) {
    const next = addKeyTerm(terms, candidate, maxLength);
    setDraft('');
    if (next.length !== terms.length) onChange(serializeKeyTerms(next));
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // Vírgula é o separador do contrato: digitá-la confirma o termo em vez de
    // entrar no texto e quebrar a serialização.
    if (event.key !== 'Enter' && event.key !== ',') return;
    // Sem o preventDefault do Enter o formulário inteiro seria submetido.
    event.preventDefault();
    commitDraft(draft);
  }

  function handleRemove(term: string) {
    onChange(serializeKeyTerms(terms.filter(current => current !== term)));
  }

  return (
    <div className="flex flex-col gap-nano">
      <TextField
        label={label}
        size="md"
        fullWidth
        {...(placeholder !== undefined ? { placeholder } : {})}
        {...(helper !== undefined ? { helper } : {})}
        trailingIcon={PlusIcon}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={handleKeyDown}
        // O termo digitado e não confirmado seria perdido ao sair do campo.
        onBlur={() => commitDraft(draft)}
      />

      {terms.length > 0 && (
        <ul className="flex flex-wrap gap-quark" aria-label={label}>
          {terms.map(term => (
            <li key={term}>
              <TagSelect
                mode="removable"
                size="sm"
                label={term}
                ariaLabelRemovable={t('edit.settings.keyTerms.remove', { term })}
                onClickRemovable={() => handleRemove(term)}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
